import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faChevronDown, faUserMinus, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { ROLES } from '../services/adminService';
import { linkDeWhatsApp } from '../utils/telefono';
import SuscripcionNegocio, { estadoSuscripcion, ordenSuscripciones, necesitaAtencion } from './SuscripcionNegocio';
import './VistaComo.css';

// Barra del superadmin para ver el home como lo ve cualquier usuario (como el
// "View as" de Glide). Solo se muestra si la sesión tiene el claim de superadmin;
// la vista es de solo lectura.

function estadoMp(mp) {
    if (!mp) return { texto: 'Sin cuenta de MP', clase: 'neutro' };
    if (mp.status === 'needs_reauth') return { texto: 'MP pide reconexión', clase: 'alerta' };
    if (mp.tipo === 'manual') return { texto: 'MP manual', clase: 'neutro' };
    return { texto: 'MP activa', clase: 'ok' };
}

const pesos = (monto) => `$${Number(monto).toLocaleString('es-AR')}`;

// Con el home abierto, el backend deja una marca de uso cada 10 minutos
// (registrarUso): si la última es de hace menos que esto, el usuario está activo.
const ACTIVO_MS = 15 * 60 * 1000;

function hace(ms) {
    const minutos = Math.round(ms / 60000);
    if (minutos < 60) return `hace ${Math.max(minutos, 1)} min`;
    const horas = Math.round(minutos / 60);
    if (horas < 24) return `hace ${horas} h`;
    const dias = Math.round(horas / 24);
    if (dias < 60) return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`;
    const meses = Math.round(dias / 30);
    if (meses < 24) return `hace ${meses} meses`;
    return `hace ${Math.round(dias / 365)} años`;
}

// "Activo ahora" si tiene el home abierto y a la vista; si no, cuándo se lo vio por
// última vez. Para quien todavía no tiene marcas de uso se toma la última renovación
// de su sesión, que pasa cada hora con el home abierto (por eso es aproximada).
function actividad(usuario, ahora) {
    const fecha = (iso) => (iso ? Date.parse(iso) : 0);
    const uso = fecha(usuario.ultimoUso);
    if (uso && ahora - uso < ACTIVO_MS) {
        return ahora - fecha(usuario.ultimoVisible) < ACTIVO_MS
            ? { texto: 'Activo ahora', clase: 'activo' }
            : { texto: 'Abierto en segundo plano', clase: 'fondo' };
    }
    const visto = Math.max(uso, fecha(usuario.ultimaSesion));
    if (!visto) return { texto: 'Nunca entró', clase: 'nunca' };
    return {
        texto: `Visto ${hace(ahora - visto)}`,
        clase: 'visto',
        detalle: new Date(visto).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
    };
}

function Actividad({ usuario, ahora }) {
    const a = actividad(usuario, ahora);
    return (
        <span className={`vista-como__actividad vista-como__actividad--${a.clase}`} title={a.detalle}>
            <span className="vista-como__punto" aria-hidden="true" />
            {a.texto}
        </span>
    );
}

// Monto máximo de las transferencias que ven los operadores de la cuenta que no
// tienen uno propio (se lo pone su admin desde la gestión de operadores). El admin
// principal y los administradores siguen viendo todo (ver adminFijarLimite).
function LimiteOperadores({ negocio, onGuardar }) {
    const [editando, setEditando] = useState(false);
    const [valor, setValor] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);

    const abrir = () => {
        setValor(negocio.limite_operadores ? String(negocio.limite_operadores) : '');
        setError(null);
        setEditando(true);
    };

    const guardar = async (limite) => {
        setGuardando(true);
        setError(null);
        try {
            await onGuardar(negocio.collector_id, limite);
            setEditando(false);
        } catch (e) {
            setError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    if (!editando) {
        return (
            <div className="vista-como__limite">
                <span>
                    Operadores sin límite propio ven:{' '}
                    <strong>{negocio.limite_operadores ? `hasta ${pesos(negocio.limite_operadores)}` : 'todas'}</strong>
                </span>
                <button className="vista-como__limite-link" onClick={abrir}>Cambiar</button>
            </div>
        );
    }

    const monto = Number(valor);
    return (
        <form
            className="vista-como__limite vista-como__limite--editando"
            onSubmit={(e) => {
                e.preventDefault();
                if (monto > 0) guardar(monto);
            }}
        >
            <span>Operadores ven hasta $</span>
            <input
                autoFocus
                type="text"
                inputMode="numeric"
                className="vista-como__limite-input"
                placeholder="200000"
                value={valor}
                onChange={(e) => setValor(e.target.value.replace(/\D/g, '').slice(0, 9))}
                disabled={guardando}
            />
            <button type="submit" className="vista-como__boton" disabled={guardando || !(monto > 0)}>
                {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            {negocio.limite_operadores && (
                <button type="button" className="vista-como__boton" onClick={() => guardar(null)} disabled={guardando}>
                    Sin límite
                </button>
            )}
            <button type="button" className="vista-como__limite-link" onClick={() => setEditando(false)} disabled={guardando}>
                Cancelar
            </button>
            {error && <span className="vista-como__limite-error">{error}</span>}
        </form>
    );
}

// Alias de la cuenta de Mercado Pago que el home les muestra a todos los usuarios
// del negocio. Mercado Pago no lo informa: se carga a mano (ver adminFijarAlias) o
// llega solo por el CVU, oculto hasta que un admin de la cuenta lo muestra.
function AliasNegocio({ negocio, onGuardar }) {
    const [editando, setEditando] = useState(false);
    const [valor, setValor] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);

    const abrir = () => {
        setValor(negocio.alias || '');
        setError(null);
        setEditando(true);
    };

    const guardar = async (alias) => {
        setGuardando(true);
        setError(null);
        try {
            await onGuardar(negocio.collector_id, alias);
            setEditando(false);
        } catch (e) {
            setError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    if (!editando) {
        return (
            <div className="vista-como__limite">
                <span>
                    Alias de MP: <strong>{negocio.alias || 'sin cargar'}</strong>
                    {negocio.alias && negocio.mostrar_alias === false && ' (oculto en el panel)'}
                </span>
                <button className="vista-como__limite-link" onClick={abrir}>
                    {negocio.alias ? 'Cambiar' : 'Cargar'}
                </button>
            </div>
        );
    }

    const alias = valor.trim();
    return (
        <form
            className="vista-como__limite vista-como__limite--editando"
            onSubmit={(e) => {
                e.preventDefault();
                if (alias) guardar(alias);
            }}
        >
            <span>Alias</span>
            <input
                autoFocus
                type="text"
                className="vista-como__limite-input vista-como__alias-input"
                placeholder="nombre.apellido"
                value={valor}
                onChange={(e) => setValor(e.target.value.replace(/[^a-zA-Z0-9.-]/g, '').slice(0, 20))}
                disabled={guardando}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
            />
            <button type="submit" className="vista-como__boton" disabled={guardando || !alias}>
                {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            {negocio.alias && (
                <button type="button" className="vista-como__boton" onClick={() => guardar(null)} disabled={guardando}>
                    Quitar
                </button>
            )}
            <button type="button" className="vista-como__limite-link" onClick={() => setEditando(false)} disabled={guardando}>
                Cancelar
            </button>
            {error && <span className="vista-como__limite-error">{error}</span>}
        </form>
    );
}

// Filtro de monto de un operador, lo haya puesto el superadmin o su admin: el propio
// o, si no tiene, el de la cuenta (como limiteDeMonto en functions). Nada para los
// que ven todo.
function FiltroDeMonto({ usuario, negocio }) {
    if (usuario.rol !== 'operador') return null;
    if (usuario.limite_monto) {
        return <span className="vista-como__filtro">Hasta {pesos(usuario.limite_monto)}</span>;
    }
    if (!negocio.limite_operadores) return null;
    return (
        <span className="vista-como__filtro vista-como__filtro--cuenta" title="Límite de la cuenta: no tiene uno propio">
            Hasta {pesos(negocio.limite_operadores)} (cuenta)
        </span>
    );
}

// Abre un chat de WhatsApp con el usuario. Es un botón y no un link: el Meta Pixel mira
// los clicks de la página y con un link podría mandarle a Meta el número del cliente.
function BotonWhatsApp({ usuario }) {
    if (!usuario.telefono) {
        return (
            <button className="vista-como__whatsapp" disabled title="Sin teléfono cargado" aria-label={`${usuario.email} no tiene teléfono cargado`}>
                <FontAwesomeIcon icon={faWhatsapp} />
            </button>
        );
    }
    return (
        <button
            className="vista-como__whatsapp"
            title="Escribir por WhatsApp"
            aria-label={`Escribirle por WhatsApp a ${usuario.fullName || usuario.email}`}
            onClick={() => window.open(linkDeWhatsApp(usuario.telefono), '_blank', 'noopener,noreferrer')}
        >
            <FontAwesomeIcon icon={faWhatsapp} />
        </button>
    );
}

function coincide(usuario, negocio, texto) {
    if (!texto) return true;
    const buscado = texto.toLowerCase();
    return [usuario.email, usuario.fullName, usuario.tienda, negocio.nombre, ROLES[usuario.rol]]
        .some((valor) => (valor || '').toLowerCase().includes(buscado));
}

function VistaComoBar({ negocios, cargando, error, miEmail, usuarioVista, negocioVista, onElegir, onBaja, onRecargar, onFijarLimite, onFijarAlias, onSuscripcion }) {
    const [abierto, setAbierto] = useState(false);
    // El panel lista usuarios ("Ver como") o el seguimiento de suscripciones.
    const [modo, setModo] = useState('usuarios');
    const [busqueda, setBusqueda] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        if (!abierto) return undefined;

        const alClickAfuera = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
        };
        const alApretarEscape = (e) => {
            if (e.key === 'Escape') setAbierto(false);
        };
        document.addEventListener('mousedown', alClickAfuera);
        document.addEventListener('keydown', alApretarEscape);
        return () => {
            document.removeEventListener('mousedown', alClickAfuera);
            document.removeEventListener('keydown', alApretarEscape);
        };
    }, [abierto]);

    const filtrados = useMemo(() => {
        const texto = busqueda.trim();
        return negocios
            .map((negocio) => ({ ...negocio, usuarios: negocio.usuarios.filter((u) => coincide(u, negocio, texto)) }))
            .filter((negocio) => negocio.usuarios.length > 0);
    }, [negocios, busqueda]);

    // Negocios con cuenta de Mercado Pago, para el seguimiento de suscripciones.
    const conCuenta = useMemo(() => {
        const texto = busqueda.trim().toLowerCase();
        return negocios
            .filter((n) => n.mp && (!texto || n.nombre.toLowerCase().includes(texto)))
            .sort(ordenSuscripciones);
    }, [negocios, busqueda]);

    // Para cobrar, con un cobro rechazado, pausados o cancelados: esperan algo de Martín.
    const paraCobrar = negocios.filter((n) => necesitaAtencion(n.suscripcion)).length;

    const abrirEn = (nuevoModo) => {
        if (abierto && modo === nuevoModo) {
            setAbierto(false);
            return;
        }
        // Al abrir se vuelve a pedir la lista: así el "Activo ahora" está al día.
        if (!abierto && !cargando) onRecargar();
        setModo(nuevoModo);
        setBusqueda('');
        setAbierto(true);
    };

    const ahora = Date.now();

    const elegir = (email) => {
        onElegir(email === miEmail ? null : email);
        setAbierto(false);
        setBusqueda('');
    };

    return (
        <div className={`vista-como ${usuarioVista ? 'vista-como--ajena' : ''}`} ref={ref}>
            <div className="vista-como__barra">
                <span className="vista-como__resumen">
                    <FontAwesomeIcon icon={faEye} className="vista-como__icono" />
                    <span>
                        {usuarioVista ? (
                            <>
                                Viendo como <strong>{usuarioVista.fullName || usuarioVista.email}</strong>
                                {' · '}{ROLES[usuarioVista.rol]}
                                {negocioVista && <>{' · '}{negocioVista}</>}
                                {' · solo lectura'}
                            </>
                        ) : (
                            'Administrador general · estás viendo tu cuenta'
                        )}
                    </span>
                </span>
                <span className="vista-como__acciones">
                    <button className="vista-como__boton" onClick={() => abrirEn('usuarios')}>
                        {usuarioVista ? 'Cambiar' : 'Ver como'} <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                    <button
                        className="vista-como__boton"
                        onClick={() => abrirEn('suscripciones')}
                        aria-label={paraCobrar ? `Suscripciones: ${paraCobrar} para atender` : 'Suscripciones'}
                        title={paraCobrar ? `${paraCobrar} para atender (cobrar, cobro rechazado o cancelada)` : 'Seguimiento de suscripciones'}
                    >
                        <FontAwesomeIcon icon={faCreditCard} /> Suscripciones
                        {paraCobrar > 0 && <span className="suscripcion__contador">{paraCobrar}</span>}
                    </button>
                    {usuarioVista && (
                        <button className="vista-como__boton" onClick={() => elegir(null)}>
                            Volver a mí
                        </button>
                    )}
                </span>
            </div>

            {abierto && (
                <div className="vista-como__panel">
                    <input
                        autoFocus
                        type="text"
                        className="vista-como__buscar"
                        placeholder={modo === 'usuarios' ? 'Buscar por email, nombre, negocio o rol' : 'Buscar negocio'}
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />

                    {cargando && <div className="vista-como__vacio">Cargando usuarios…</div>}

                    {error && (
                        <div className="vista-como__error">
                            {error}
                            <button className="vista-como__boton" onClick={onRecargar}>Reintentar</button>
                        </div>
                    )}

                    {modo === 'suscripciones' && !cargando && !error && (
                        <>
                            {conCuenta.length === 0 && (
                                <div className="vista-como__vacio">Ningún negocio coincide con la búsqueda.</div>
                            )}
                            {conCuenta.map((negocio) => (
                                <div key={negocio.collector_id} className="vista-como__negocio">
                                    <div className="vista-como__negocio-titulo">
                                        <span>{negocio.nombre}</span>
                                    </div>
                                    <SuscripcionNegocio negocio={negocio} onAccion={onSuscripcion} />
                                </div>
                            ))}
                        </>
                    )}

                    {modo === 'usuarios' && !cargando && !error && filtrados.length === 0 && (
                        <div className="vista-como__vacio">Ningún usuario coincide con la búsqueda.</div>
                    )}

                    {modo === 'usuarios' && filtrados.map((negocio) => {
                        const mp = estadoMp(negocio.mp);
                        const suscripcion = negocio.suscripcion && estadoSuscripcion(negocio.suscripcion);
                        return (
                            <div key={negocio.collector_id} className="vista-como__negocio">
                                <div className="vista-como__negocio-titulo">
                                    <span>{negocio.nombre}</span>
                                    <span className="vista-como__negocio-estados">
                                        {suscripcion && (
                                            <span className={`suscripcion__estado suscripcion__estado--${suscripcion.clase}`}>
                                                {suscripcion.texto}
                                            </span>
                                        )}
                                        <span className={`vista-como__mp vista-como__mp--${mp.clase}`}>{mp.texto}</span>
                                    </span>
                                </div>
                                {negocio.mp && <AliasNegocio negocio={negocio} onGuardar={onFijarAlias} />}
                                {negocio.mp && <LimiteOperadores negocio={negocio} onGuardar={onFijarLimite} />}
                                {negocio.usuarios.map((u) => (
                                    <div
                                        key={u.email}
                                        className={`vista-como__fila ${u.email === usuarioVista?.email ? 'is-activo' : ''}`}
                                    >
                                        <button className="vista-como__usuario" onClick={() => elegir(u.email)}>
                                            <span className="vista-como__usuario-datos">
                                                <span className="vista-como__nombre">
                                                    {u.fullName || u.email}
                                                    {u.email === miEmail && ' (vos)'}
                                                </span>
                                                <span className="vista-como__email">{u.email}</span>
                                            </span>
                                            <Actividad usuario={u} ahora={ahora} />
                                            <FiltroDeMonto usuario={u} negocio={negocio} />
                                            <span className={`vista-como__rol vista-como__rol--${u.rol}`}>{ROLES[u.rol]}</span>
                                        </button>
                                        <BotonWhatsApp usuario={u} />
                                        {u.email !== miEmail ? (
                                            <button
                                                className="vista-como__baja"
                                                title="Dar de baja"
                                                aria-label={`Dar de baja a ${u.email}`}
                                                onClick={() => {
                                                    setAbierto(false);
                                                    onBaja(u.email);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faUserMinus} />
                                            </button>
                                        ) : (
                                            <span className="vista-como__baja vista-como__baja--vacia" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default VistaComoBar;
